import { FormControl, Grid, Stack, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Box, IconButton, LinearProgress, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useFormik } from 'formik';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { io } from 'socket.io-client';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { JOINED_ROOM, JOIN_ALREADY_ENDED_ROOM, WEB_AST_CALL_INITIATION } from '../../Common/helper';
import CadisButton from '../../Components/Input/CadisButton';
import CadisTextField from '../../Components/Input/CadisTextField';
import CadisLoader from '../../Components/Loading/CadisLoader';
import apis from '../../HttpConfig/Api';
import { useSocketContext } from '../../context/SocketContext';

// Audio file path
const MediaVolumeTestAudio = '/audio/Media_Volume_Test_Audio.mp3';

// Volume image
import volumeImage from "../../Assets/Images/volume.jpg";

// Styled play button
const PlayMusicButton = styled(IconButton)(({ theme }) => ({
    width: 64,
    height: 64,
    padding: 0,
    borderRadius: '50%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    border: `2px solid ${theme.palette.primary.contrastText}`,
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        transform: 'scale(1.06)',
        boxShadow: theme.shadows[8],
    },
    '&:disabled': {
        backgroundColor: theme.palette.action.disabled,
        color: theme.palette.action.disabled,
    },
    transition: 'all 0.3s ease',
    boxShadow: theme.shadows[4],
}));

const WebAssistantJoin = () => {

    const { t } = useTranslation();
    const params = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const { socketRef, setAgoraConfig } = useSocketContext(); // socket context customeHook

    // Audio test states
    const [showAudioTestDialog, setShowAudioTestDialog] = useState(false);
    const [audioTestPassed, setAudioTestPassed] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    // Debug: Log state changes
    useEffect(() => {
        console.log('showAudioTestDialog changed to:', showAudioTestDialog);
    }, [showAudioTestDialog]);

    useEffect(() => {
        console.log('audioTestPassed changed to:', audioTestPassed);
    }, [audioTestPassed]);

    // Restrict browser back button on this screen
    useEffect(() => {
        // Capture THIS screen's URL at mount time. We must re-push this exact
        // URL later, because inside a popstate handler window.location.href has
        // already changed to the previous entry (the call screen).
        const joinScreenUrl = window.location.href;

        // Push a duplicate entry so the first back press has something to consume.
        window.history.pushState({ page: 'webAssistantJoin' }, '', joinScreenUrl);

        const handlePopState = (event: PopStateEvent) => {
            event.stopPropagation();
            // Cancel the back navigation by re-pushing the join screen URL.
            window.history.pushState({ page: 'webAssistantJoin' }, '', joinScreenUrl);
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    // Check if audio test was already passed in this session
    useEffect(() => {
        // Clear any previous audio test completion to ensure popup shows (for debugging)
        sessionStorage.removeItem('audioTestCompleted');

        const audioTestCompleted = sessionStorage.getItem('audioTestCompleted');
        const comingFromCall = sessionStorage.getItem('comingFromCall'); // Check if user is returning from a call

        if (audioTestCompleted === 'true') {
            setAudioTestPassed(true);
        } else if (comingFromCall === 'true') {
            // If coming from a call, assume audio test was already done
            setAudioTestPassed(true);
            sessionStorage.setItem('audioTestCompleted', 'true');
        } else {
            console.log('Audio test not completed, showing popup automatically');
            // Show audio test popup automatically when page loads
            setTimeout(() => {
                setShowAudioTestDialog(true);
            }, 500); // Small delay to ensure page is loaded
        }

        // Clear the comingFromCall flag after checking
        if (comingFromCall === 'true') {
            sessionStorage.removeItem('comingFromCall');
        }
    }, []);

    // Audio test functions

    const playTestAudio = async () => {
        try {
            setIsPlaying(true);
            setProgress(0);

            // Create audio element if not exists
            if (!audioRef.current) {
                audioRef.current = new Audio();
                audioRef.current.crossOrigin = "anonymous";
            }

            // Use imported audio file
            audioRef.current.src = MediaVolumeTestAudio;
            audioRef.current.volume = 0.7; // Fixed volume
            audioRef.current.currentTime = 0;

            await audioRef.current.play();

            // Progress tracking
            const duration = 4; // 4 seconds
            const interval = 100; // Update every 100ms
            const step = 100 / (duration * 1000 / interval); // Progress increment

            let currentProgress = 0;
            const progressTimer = setInterval(() => {
                currentProgress += step;
                if (currentProgress >= 100) {
                    currentProgress = 100;
                    clearInterval(progressTimer);
                }
                setProgress(currentProgress);
            }, interval);

            // Auto-stop after 4 seconds but DON'T auto-close dialog
            setTimeout(() => {
                if (audioRef.current) {
                    audioRef.current.pause();
                }
                setIsPlaying(false);
                setProgress(0);
                clearInterval(progressTimer);
            }, 4500);

        } catch (error) {
            setIsPlaying(false);
            setProgress(0);
        }
    };

    const handleAudioTestSuccess = () => {
        // Stop audio immediately when user confirms they can hear it
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setIsPlaying(false);
        setProgress(0);
        setAudioTestPassed(true);
        setShowAudioTestDialog(false);
        sessionStorage.setItem('audioTestCompleted', 'true');
        toast.success('Audio test passed! You can now join the call.');
    };

    useEffect(() => {
        socketRef.current?.on(JOIN_ALREADY_ENDED_ROOM, () => {
            toast.error(t('responseMsg.msg_theLinkHasExpired'));
            setLoading(false);
        });

        socketRef.current?.on(JOINED_ROOM, (data) => {
            // AGORA: Set Agora config from room data
            if (data?.appId) {
                setAgoraConfig({ appId: data.appId, channel: data.channel, token: data.token });
            }
            // WAST always joins conference calls - set flag so audio gating works correctly
            sessionStorage.setItem('isConferenceCall', JSON.stringify(true));
            // Set flag to indicate user is going to call
            sessionStorage.setItem('comingFromCall', 'true');
            navigate("/webassistantcall", {
                state: {
                    callSessionId: params.sessionId
                }
            })
        });

        // unmount function to cleare session id from sessionstorage
        return () => {
            socketRef.current?.off(JOINED_ROOM);
            socketRef.current?.off(JOIN_ALREADY_ENDED_ROOM);
            sessionStorage.removeItem("WastSessionId");
            // Cleanup audio
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        }

    }, [socketRef.current])

    const { values, handleSubmit, errors, touched, handleChange } = useFormik({
        initialValues: {
            userName: ''
        },
        onSubmit: async (values, props) => {
            // Check if audio test is required
            if (!audioTestPassed) {
                setShowAudioTestDialog(true);
                return;
            }

            setLoading(true);
            onJoinCall();
        },
    });

    const onJoinCall = () => {
        if (!socketRef.current && apis.socketURL) {
            const token = sessionStorage.getItem('token');
            const socketAuth = {
                query: { token },
            };
            socketRef.current = io(apis.socketURL, socketAuth);
            socketRef.current.on('connect', () => {
                console.log('Socket connected');
            });
        }
        const linkRoomID = params.sessionId !== undefined ? params.sessionId : '';
        socketRef.current?.emit(WEB_AST_CALL_INITIATION, {
            webAssistantName: values.userName,
            sessionId: linkRoomID,
        });
    }

    return (
        <>
            <div>
                <Typography className="mt-0 mb-5 prelogin-title" sx={(theme) => theme.typography.h3}>{t('lbl_joinCall')}</Typography>

                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <FormControl variant="outlined">
                            <label className='form-label '>
                                {t('lbl_enterName')}
                            </label>
                            <CadisTextField autoComplete="false" value={values.userName} name="userName" type="text" onChange={handleChange}
                                error={touched.userName && errors.userName ? true : false}
                                helperText={touched.userName && t(`${errors.userName ? errors.userName : ''}`)}
                            />
                        </FormControl>
                        <Grid container spacing={0} alignItems="center" >
                            <Grid item xs>
                                <CadisButton type='submit' variant="contained" color='primary'
                                    disabled={loading} endIcon={loading ? <CadisLoader color='primary' size={14} /> : ''}>
                                    {t('lbl_join')}
                                </CadisButton>
                            </Grid>
                        </Grid>
                    </Stack>
                </form>

                {/* Audio Test Dialog */}
                <Dialog
                    open={showAudioTestDialog}
                    onClose={() => {
                        // Prevent closing without completing test
                        return null;
                    }}
                    aria-labelledby="audio-test-dialog-title"
                    aria-describedby="audio-test-dialog-description"
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle id="audio-test-dialog-title">
                        <Box display="flex" alignItems="center" gap={1}>
                            <VolumeUpIcon color="primary" />
                            Audio Test
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box display="flex" flexDirection="column" gap={3}>

                            {/* Audio Test Instructions */}
                            <Box>
                                <Typography
                                    variant="body1"
                                    id="audio-test-dialog-description"
                                    fontSize={16}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
                                >
                                    Play to test your sound volume. Increase <strong>"Media Volume"</strong> as needed.
                                </Typography>
                            </Box>

                            {/* Volume Image */}
                            <Box
                                sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
                            >
                                <Box
                                    component="img"
                                    src={volumeImage}
                                    alt="Volume settings guide"
                                    sx={{ width: { xs: '100%', sm: '80%' }, maxWidth: '500px', height: 'auto', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                                    onError={(e) => {
                                        console.log('Volume image failed to load');
                                        (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                />
                            </Box>
                            {/* Play Button */}
                            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                {/* Play button and Progress bar in horizontal line */}
                                <Box display="flex" alignItems="center" gap={2} sx={{ width: '100%' }}>
                                    <Tooltip title="Play Audio" arrow>
                                        <PlayMusicButton
                                            onClick={playTestAudio}
                                            disabled={isPlaying}
                                        >
                                            <Typography
                                                component="span"
                                                sx={{ fontSize: 10, fontWeight: 800, lineHeight: 1, letterSpacing: 1 }}
                                            >
                                                PUSH
                                            </Typography>
                                            <PlayArrowIcon sx={{ fontSize: 26, mt: -0.25 }} />
                                        </PlayMusicButton>
                                    </Tooltip>

                                    {/* Progress Bar - perfectly aligned with 35px button center */}
                                    <Box sx={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        height: '35px'  // Match play button height for perfect vertical alignment
                                    }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={progress}
                                            sx={{
                                                width: '100%', height: 6, borderRadius: 3, backgroundColor: isPlaying ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.5)', '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: isPlaying ? (theme) => theme.palette.primary.main : 'rgba(0, 0, 0, 0.2)', transition: 'background-color 0.3s ease', }, opacity: isPlaying ? 1 : 0.6, transition: 'opacity 0.3s ease',
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <CadisButton
                            onClick={handleAudioTestSuccess}
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            size="large"
                        >
                            I Can Hear It!
                        </CadisButton>
                    </DialogActions>
                </Dialog>
            </div>
        </>
    )
}

export default WebAssistantJoin